import { Button, Card, Popconfirm, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { usePermission } from "@/shared/hooks/usePermission";
import {
	useArchiveDepartment,
	useDeleteDepartment,
	useDepartmentsList,
	useMoveDepartment,
	useUnarchiveDepartment,
	useUpdateDepartment,
} from "../hooks/useDepartments";
import type { DepartmentDetail, DepartmentFormValues } from "../types";
import { DepartmentFormModal } from "./DepartmentFormModal";
import { MoveDepartmentModal } from "./MoveDepartmentModal";

interface DepartmentSettingsTabProps {
	department: DepartmentDetail;
	/**
	 * O'chirish MUVAFFAQIYATLI bo'lgach chaqiriladi — sahifa endi mavjud
	 * bo'lmagan bo'lim profilida qolmasligi uchun (`DepartmentDetailPage`
	 * ota bo'limga yoki `/departments` ga o'tkazadi).
	 */
	onDeleted?: () => void;
}

/**
 * Bo'lim profilining "Sozlamalar" tabi — mavjud `DepartmentFormModal` /
 * `MoveDepartmentModal` ni QAYTA YOZMASDAN tugmalar bilan ochadi va
 * "Arxivlash" / "Arxivdan chiqarish" / "O'chirish" amallarini beradi (spec §8,
 * §6.3a).
 *
 * ⚠️ Tugmalar ruxsat ortida — ruxsat bo'lmasa UMUMAN chizilmaydi
 * (`departments:update` — Tahrirlash/Ko'chirish, `departments:archive` —
 * Arxivlash va Arxivdan chiqarish, `departments:delete` — O'chirish).
 * "Xavfli hudud" kartasi ham faqat kamida bitta amal bo'lganda chiziladi.
 *
 * ⚠️ Faol bo'limda "Arxivlash", arxivlanganda "Arxivdan chiqarish" chiziladi.
 * Ikkalasi ham, "O'chirish" ham `Popconfirm` ortida. Rad etish (409)
 * xabarlari mutatsiya hooklari ichida ko'rsatiladi.
 */
export function DepartmentSettingsTab({ department, onDeleted }: DepartmentSettingsTabProps) {
	const { t } = useTranslation("departments");
	const { can } = usePermission();
	const [editOpen, setEditOpen] = useState(false);
	const [moveOpen, setMoveOpen] = useState(false);

	// Forma va Ko'chirish modallari `DepartmentTreeSelect` uchun to'liq,
	// sahifalanmagan ro'yxatga tayanadi (`DepartmentSubunitsTab` bilan bir xil).
	const { data: departments = [] } = useDepartmentsList();
	const updateDepartment = useUpdateDepartment();
	const moveDepartment = useMoveDepartment();
	const archiveDepartment = useArchiveDepartment();
	const unarchiveDepartment = useUnarchiveDepartment();
	const deleteDepartment = useDeleteDepartment();

	const arxivAmali = can("departments", "archive");
	const ochirish = can("departments", "delete");

	const handleSubmit = (values: DepartmentFormValues) => {
		// Ota-onani shu forma orqali o'zgartirib bo'lmaydi — buning uchun
		// alohida "Ko'chirish" modali bor.
		updateDepartment.mutate(
			{
				id: department.id,
				input: { name: values.name, gradeId: values.gradeId, code: values.code },
			},
			{ onSuccess: () => setEditOpen(false) }
		);
	};

	return (
		<div className="flex flex-col gap-4 max-w-2xl">
			<Card title={t("settingsTab.basicInfo")}>
				<Space orientation="vertical" className="w-full">
					<div>
						<span className="text-xs text-gray-400">{t("form.nameLabel")}</span>
						<div>{department.name}</div>
					</div>
					<PermissionGate resource="departments" action="update">
						<Space>
							<Button onClick={() => setEditOpen(true)}>{t("detail.editButton")}</Button>
							<Button onClick={() => setMoveOpen(true)}>{t("detail.moveButton")}</Button>
						</Space>
					</PermissionGate>
				</Space>
			</Card>

			{arxivAmali || ochirish ? (
				<Card title={t("settingsTab.dangerZone")}>
					<Space wrap>
						{arxivAmali && department.isActive ? (
							<Popconfirm
								title={t("settingsTab.archiveConfirmTitle")}
								description={t("settingsTab.archiveConfirmDescription")}
								okText={t("detail.archiveButton")}
								okButtonProps={{ danger: true }}
								onConfirm={() => archiveDepartment.mutate(department.id)}
							>
								<Button danger loading={archiveDepartment.isPending}>
									{t("detail.archiveButton")}
								</Button>
							</Popconfirm>
						) : null}
						{arxivAmali && !department.isActive ? (
							<Popconfirm
								title={t("settingsTab.unarchiveConfirmTitle")}
								description={t("settingsTab.unarchiveConfirmDescription")}
								okText={t("detail.unarchiveButton")}
								onConfirm={() => unarchiveDepartment.mutate(department.id)}
							>
								<Button loading={unarchiveDepartment.isPending}>
									{t("detail.unarchiveButton")}
								</Button>
							</Popconfirm>
						) : null}
						{ochirish ? (
							<Popconfirm
								title={t("settingsTab.deleteConfirmTitle")}
								description={t("settingsTab.deleteConfirmDescription")}
								okText={t("detail.deleteButton")}
								okButtonProps={{ danger: true }}
								onConfirm={() =>
									deleteDepartment.mutate(department.id, { onSuccess: () => onDeleted?.() })
								}
							>
								<Button danger type="primary" loading={deleteDepartment.isPending}>
									{t("detail.deleteButton")}
								</Button>
							</Popconfirm>
						) : null}
					</Space>
				</Card>
			) : null}

			<DepartmentFormModal
				open={editOpen}
				editing={department}
				departments={departments}
				isSubmitting={updateDepartment.isPending}
				onCancel={() => setEditOpen(false)}
				onSubmit={handleSubmit}
			/>
			<MoveDepartmentModal
				open={moveOpen}
				department={department}
				departments={departments}
				isSubmitting={moveDepartment.isPending}
				onCancel={() => setMoveOpen(false)}
				onSubmit={(parentId) =>
					moveDepartment.mutate(
						{ id: department.id, parentId },
						{ onSuccess: () => setMoveOpen(false) }
					)
				}
			/>
		</div>
	);
}
